'use client';

import {
    getFacebookEmbedUrl,
    isFacebookVideoUrl,
    isVideoUrl,
} from '@/utils/isVideoUrl';
import { parseImagePosition } from '@/utils/parseImagePosition';
import { normalizeSvgFileForUpload } from '@/utils/normalizeSvgUpload';
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import ReactPlayer from 'react-player'; // ReactPlayer is back!
import { useEditor } from './context/EditorContext/EditorContext';
import { useNotifications } from './context/NotificationContext/NotificationContext';

/* ----------------------------- URL helpers ----------------------------- */

const isEmbedCode = (val) =>
    typeof val === 'string' &&
    /<\s*(iframe|script|video|svg|div|section|figure|blockquote)[\s>]/i.test(
        val
    );
const POSITION_POINTS = [0, 50, 100];

function clampPercent(value) {
    return Math.min(100, Math.max(0, value));
}

function formatImagePosition(position) {
    const x = Math.round(clampPercent(position.x));
    const y = Math.round(clampPercent(position.y));
    return `${x} ${y}`;
}

function snapToNearest(value, points = POSITION_POINTS) {
    return points.reduce((closest, point) =>
        Math.abs(point - value) < Math.abs(closest - value) ? point : closest
    );
}

function PositionField({ label, value, onChange }) {
    const handleStep = (delta) => {
        const base = Number.isFinite(value) ? value : 50;
        onChange(clampPercent(base + delta));
    };

    return (
        <div className="flex items-center gap-1.5">
            <span className="w-7 text-right text-[9px] font-semibold text-neutral-600">
                {label}
            </span>
            <div className="overflow-hidden rounded-[8px] bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                <div className="flex h-5 items-center rounded-[7px] bg-white px-0">
                    <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min="0"
                        max="100"
                        step="1"
                        value={Number.isFinite(value) ? Math.round(value) : 50}
                        onChange={(event) => {
                            const next = parseFloat(event.target.value);
                            if (Number.isNaN(next)) return;
                            onChange(next);
                        }}
                        className="ml-[6px] w-[18px] border-0 bg-transparent px-0 text-right text-[9px] tabular-nums text-neutral-800 outline-none focus:outline-none focus:ring-0 focus-visible:outline-none"
                    />
                    <span className="ml-[2px] text-[9px] font-semibold text-neutral-500">
                        %
                    </span>
                    <div className="ml-1 flex flex-col items-center justify-center gap-[1px]">
                        <button
                            type="button"
                            onClick={() => handleStep(1)}
                            aria-label={`${label} increase`}
                            className="h-[5px] w-[8px] text-neutral-500 hover:text-neutral-700"
                        >
                            <svg
                                viewBox="0 0 8 6"
                                width="7"
                                height="5"
                                aria-hidden="true"
                            >
                                <path d="M4 0L8 6H0L4 0Z" fill="currentColor" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            onClick={() => handleStep(-1)}
                            aria-label={`${label} decrease`}
                            className="h-[5px] w-[8px] text-neutral-500 hover:text-neutral-700"
                        >
                            <svg
                                viewBox="0 0 8 6"
                                width="7"
                                height="5"
                                aria-hidden="true"
                            >
                                <path d="M0 0H8L4 6L0 0Z" fill="currentColor" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PositionPad({ position, onChange }) {
    const padRef = useRef(null);

    const applyPointer = useCallback(
        (event) => {
            if (!padRef.current) return;
            const rect = padRef.current.getBoundingClientRect();
            const relX = (event.clientX - rect.left) / rect.width;
            const relY = (event.clientY - rect.top) / rect.height;
            const next = {
                x: snapToNearest(clampPercent(relX * 100)),
                y: snapToNearest(clampPercent(relY * 100)),
            };
            onChange(next);
        },
        [onChange]
    );

    const handlePointerDown = useCallback(
        (event) => {
            event.preventDefault();
            event.currentTarget.focus?.();
            event.currentTarget.setPointerCapture?.(event.pointerId);
            applyPointer(event);
        },
        [applyPointer]
    );

    const handlePointerMove = useCallback(
        (event) => {
            if (event.buttons !== 1) return;
            applyPointer(event);
        },
        [applyPointer]
    );

    const handleKeyDown = useCallback(
        (event) => {
            const step = event.shiftKey ? 10 : 1;
            let next = null;
            if (event.key === 'ArrowLeft') {
                next = { x: position.x - step, y: position.y };
            } else if (event.key === 'ArrowRight') {
                next = { x: position.x + step, y: position.y };
            } else if (event.key === 'ArrowUp') {
                next = { x: position.x, y: position.y - step };
            } else if (event.key === 'ArrowDown') {
                next = { x: position.x, y: position.y + step };
            }
            if (!next) return;
            event.preventDefault();
            onChange({
                x: clampPercent(next.x),
                y: clampPercent(next.y),
            });
        },
        [onChange, position.x, position.y]
    );

    const snapX = snapToNearest(position.x);
    const snapY = snapToNearest(position.y);
    const xLabel = snapX === 0 ? 'Left' : snapX === 50 ? 'Center' : 'Right';
    const yLabel = snapY === 0 ? 'Top' : snapY === 50 ? 'Center' : 'Bottom';
    const activeLabel =
        snapX === 50 && snapY === 50 ? 'Center' : `${yLabel} ${xLabel}`;

    return (
        <div
            ref={padRef}
            tabIndex={0}
            role="group"
            aria-label="Image position"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onKeyDown={handleKeyDown}
            className={[
                'relative h-[52px] w-[52px] overflow-hidden rounded-[8px] bg-neutral-300 p-[2px]',
                'before:absolute before:inset-[2px] before:rounded-[7px] before:bg-white before:content-[""]',
                'focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600 focus:outline-none',
            ].join(' ')}
        >
            <span className="sr-only">
                Position {activeLabel}. Use arrow keys to nudge position. Hold
                shift for bigger steps.
            </span>
            <div className="relative z-10 h-full w-full p-1">
                <div className="grid h-full w-full grid-cols-3 grid-rows-3 place-items-center gap-[1px]">
                    {POSITION_POINTS.map((y) =>
                        POSITION_POINTS.map((x) => {
                            const isActive = x === snapX && y === snapY;
                            const xName =
                                x === 0
                                    ? 'Left'
                                    : x === 50
                                    ? 'Center'
                                    : 'Right';
                            const yName =
                                y === 0
                                    ? 'Top'
                                    : y === 50
                                    ? 'Center'
                                    : 'Bottom';
                            const label =
                                x === 50 && y === 50
                                    ? 'Center'
                                    : `${yName} ${xName}`;
                            return (
                                <button
                                    key={`${x}-${y}`}
                                    type="button"
                                    onClick={() => onChange({ x, y })}
                                    aria-pressed={isActive}
                                    aria-label={`Position ${label}`}
                                    className={[
                                        'h-[9px] w-[9px] rounded-[2px]',
                                        isActive
                                            ? 'bg-primary-600'
                                            : 'bg-neutral-400/80',
                                        'transition-colors',
                                    ].join(' ')}
                                />
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}

function ImagePositionControl({ position, onChange }) {
    return (
        <div className="flex items-start gap-1">
            <PositionPad position={position} onChange={onChange} />
            <div className="flex h-[52px] flex-col justify-between">
                <PositionField
                    label="Left"
                    value={position.x}
                    onChange={(nextX) => onChange({ x: nextX, y: position.y })}
                />
                <PositionField
                    label="Top"
                    value={position.y}
                    onChange={(nextY) => onChange({ x: position.x, y: nextY })}
                />
            </div>
        </div>
    );
}

export function ImageUpload(props) {
    const {
        company_id,
        onChange,
        file,
        element: key,
        src,
        enableVideo = true,
        onTogglePanel, // New prop to trigger drawer panel
        positionValue,
        onPositionChange,
        layout,
        midContent,
        uploadInputRef,
        removeButtonIcon = 'xmark',
        removeButtonLabel = 'Remove media',
    } = props;

    const editor = useEditor();
    const notifications = useNotifications();

    const [is_image_uploading, setIsImageUploading] = useState(false);

    const normalizeSrc = (input) => {
        if (!input) return '';
        if (typeof input === 'string') return input;
        if (typeof input === 'object') return input.url || input.src || '';
        return '';
    };
    const normalizedPropSrc = normalizeSrc(src);

    const [localSrc, setLocalSrc] = useState(normalizedPropSrc);
    const [imageHasLoaded, setImageHasLoaded] = useState(
        () => !normalizedPropSrc
    );
    const [isEditingVideo, setIsEditingVideo] = useState(
        () => enableVideo && isVideoUrl(normalizedPropSrc)
    );

    const prevSrcRef = useRef(localSrc);
    const imgElRef = useRef(null);

    const fit = String(props.fitMode || 'cover').toLowerCase();
    const fitClass = fit === 'cover' ? 'object-cover' : 'object-contain';

    const showPositionControl = typeof onPositionChange === 'function';
    const [position, setPosition] = useState(() =>
        parseImagePosition(positionValue)
    );
    const previewFitClass = showPositionControl ? 'object-cover' : fitClass;
    const previewObjectPosition = showPositionControl
        ? `${position.x}% ${position.y}%`
        : undefined;
    const actionButtonsInline = Boolean(props.actionButtonsInline);

    useEffect(() => {
        if (!showPositionControl) return;
        setPosition(parseImagePosition(positionValue));
    }, [positionValue, showPositionControl]);

    const handlePositionChange = useCallback(
        (next) => {
            const clamped = {
                x: clampPercent(next.x),
                y: clampPercent(next.y),
            };
            setPosition(clamped);
            onPositionChange?.(formatImagePosition(clamped));
        },
        [onPositionChange]
    );

    /* ---------- react to external src changes ---------- */
    useLayoutEffect(() => {
        const next = normalizedPropSrc || '';
        if (next === prevSrcRef.current) return;

        prevSrcRef.current = next;
        setLocalSrc(next);

        const isVid = enableVideo && isVideoUrl(next);
        const isEmbed = isEmbedCode(next);
        setIsEditingVideo(isVid);

        if (!next) {
            setImageHasLoaded(true);
            return;
        }
        if (isEmbed) {
            setImageHasLoaded(true);
            return;
        }
        if (isVid) {
            setImageHasLoaded(true);
            return;
        }

        setImageHasLoaded(false);
        const temp = new Image();
        temp.onload = () => setImageHasLoaded(true);
        temp.onerror = () => setImageHasLoaded(true);
        temp.src = next;
        if (temp.complete && temp.naturalWidth > 0) setImageHasLoaded(true);
        return () => {
            temp.onload = null;
            temp.onerror = null;
        };
    }, [normalizedPropSrc, enableVideo]);

    /* ---------- upload image (Direct file input) ---------- */
    const handleImageUpload = async (event) => {
        setIsImageUploading(true);
        const image = event.target.files?.[0];
        if (!image) {
            setIsImageUploading(false);
            return;
        }

        const normalizedImage = await normalizeSvgFileForUpload(image);
        const formData = new FormData();
        formData.append('image', normalizedImage);
        const url =
            `https://image-search-ytnjkd5upa-uc.a.run.app/upload/image/${company_id}?` +
            new URLSearchParams({
                filename: image.name.replace(/ +/g, '-').toLowerCase(),
            });

        try {
            const response = await fetch(url, {
                method: 'POST',
                body: formData,
            });
            if (!response.ok)
                throw new Error(`Image upload failed: ${response.statusText}`);

            const data = await response.json();
            editor.site.addImage?.({
                ...data,
                size_bytes: image.size,
            });

            onChange({ file, key, value: data.url });
            prevSrcRef.current = data.url;
            setLocalSrc(data.url);
            setIsEditingVideo(false);

            // FIX: Set loaded state immediately after successful upload
            setImageHasLoaded(true);
        } catch (error) {
            notifications.push({
                title: 'Image upload failed',
                variant: 'error',
                error,
            });
        } finally {
            setIsImageUploading(false);
        }
    };

    const handleClearMedia = useCallback(() => {
        if (!localSrc) return;
        onChange({ file, key, value: '' });
        prevSrcRef.current = '';
        setLocalSrc('');
        setIsEditingVideo(false);
        setImageHasLoaded(true);
    }, [file, key, localSrc, onChange]);

    /* ---------- ensure loaded state when image already cached ---------- */
    useEffect(() => {
        if (isEditingVideo || isEmbedCode(localSrc)) return;
        const el = imgElRef.current;
        if (localSrc && el?.complete && el.naturalWidth > 0) {
            setImageHasLoaded(true);
        }
    }, [localSrc, isEditingVideo]);

    const isPlaceholderSrc =
        typeof localSrc === 'string' &&
        localSrc.trim().startsWith('/images/placeholder.svg');
    const effectiveSrc = isPlaceholderSrc ? '' : localSrc;
    const hasMedia = !!effectiveSrc;
    const hideEmptyPreview = Boolean(props.hideEmptyPreview);
    const allowRemove = props.allowRemove !== false;
    const showPreview = !hideEmptyPreview || !!localSrc;
    const showClearButton = allowRemove && !!localSrc;
    const showPlaceholder =
        !effectiveSrc && (imageHasLoaded || isPlaceholderSrc);
    const showSkeleton =
        !!effectiveSrc &&
        !imageHasLoaded &&
        !isEditingVideo &&
        !isEmbedCode(effectiveSrc);
    const previewSize = String(props.previewSize || 'md').toLowerCase();
    const shouldRenderPreview = props.showPreview !== false && showPreview;
    const showActionButtons = props.showActionButtons !== false;
    const showAssetPickerButton = props.showAssetPickerButton !== false;
    const previewSizeClass =
        previewSize === 'logo'
            ? 'h-16 w-[16rem]'
            : previewSize === 'sm'
            ? 'h-28 w-[12rem]'
            : previewSize === 'xs'
            ? 'h-24 w-[10rem]'
            : 'h-36 w-[16rem]';
    const boxBgClass =
        !hasMedia && imageHasLoaded
            ? 'bg-gray-100'
            : hasMedia || !imageHasLoaded
            ? 'bg-transparent'
            : 'bg-gray-100';
    const layoutMode = String(layout || 'row').toLowerCase();
    const isStackedLayout = layoutMode === 'stacked' || !shouldRenderPreview;
    const controlsClassName = isStackedLayout
        ? 'flex flex-wrap items-start gap-x-8 gap-y-4'
        : 'flex h-36 flex-wrap items-end gap-x-8 gap-y-4 self-start';
    const controlsInnerClassName = isStackedLayout
        ? 'flex flex-col items-start gap-4'
        : 'flex h-full flex-col items-start justify-between';

    return (
        <div className="relative my-2 flex w-full flex-wrap gap-4 rounded-lg">
            {/* Spinner overlay */}
            {is_image_uploading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/50">
                    <svg
                        className="h-6 w-6 animate-spin text-primary-600"
                        viewBox="0 0 24 24"
                        fill="none"
                    >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                        />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        />
                    </svg>
                </div>
            )}

            {/* WRAP LAYOUT: preview + controls */}
            <div
                className={`flex w-full items-start gap-4 ${
                    isStackedLayout ? 'flex-col' : 'flex-wrap'
                } ${is_image_uploading ? 'invisible' : 'visible'}`}
            >
                {/* === PREVIEW column === */}
                {shouldRenderPreview && (
                    <div className="flex">
                        <div
                            className={`relative flex ${previewSizeClass} shrink-0 items-center justify-start overflow-hidden rounded-xl ${boxBgClass}`}
                        >
                            {showClearButton && (
                                <button
                                    type="button"
                                    onClick={handleClearMedia}
                                    aria-label={removeButtonLabel}
                                    title={removeButtonLabel}
                                    className="absolute right-2 top-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-neutral-900/80 text-white shadow transition-colors duration-150 hover:bg-neutral-900"
                                >
                                    <i
                                        className={`fas fa-${removeButtonIcon} text-[10px] leading-none`}
                                        aria-hidden="true"
                                    />
                                </button>
                            )}
                            {/* Placeholder */}
                            {showPlaceholder && (
                                <img
                                    src="/images/placeholder.svg"
                                    alt=""
                                    className="h-full w-full object-cover object-center opacity-60"
                                />
                            )}

                            {/* Skeleton */}
                            {showSkeleton && (
                                <img
                                    src="/images/placeholder.svg"
                                    alt=""
                                    className="h-full w-full animate-pulse object-cover object-center opacity-50"
                                />
                            )}

                            {/* Custom code placeholder */}
                            {effectiveSrc && isEmbedCode(effectiveSrc) && (
                                <div className="flex h-full w-full items-center justify-center rounded-md bg-neutral-100 p-4 text-xs text-neutral-600">
                                    Custom code embed
                                </div>
                            )}

                            {/* Video Thumbnail/Player */}
                            {effectiveSrc &&
                                !isEmbedCode(effectiveSrc) &&
                                enableVideo &&
                                isEditingVideo && (
                                    <div
                                        className={`relative ml-0 transition-opacity duration-300 ${
                                            imageHasLoaded
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                        }`}
                                        style={{
                                            height: '100%',
                                            width: '100%',
                                        }}
                                    >
                                        {isFacebookVideoUrl(effectiveSrc) ? (
                                            <iframe
                                                title="Facebook video preview"
                                                src={getFacebookEmbedUrl(
                                                    effectiveSrc
                                                )}
                                                className="absolute left-0 top-0 h-full w-full"
                                                allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                                                allowFullScreen
                                                loading="lazy"
                                                referrerPolicy="no-referrer-when-downgrade"
                                                onLoad={() =>
                                                    setImageHasLoaded(true)
                                                }
                                                onError={() =>
                                                    setImageHasLoaded(true)
                                                }
                                            />
                                        ) : (
                                            <ReactPlayer
                                                url={effectiveSrc}
                                                controls={true}
                                                width="100%"
                                                height="100%"
                                                // Use the wrapper prop to ensure the player's div
                                                // gets the correct absolute positioning within the container
                                                wrapper={({ children }) => (
                                                    <div className="absolute left-0 top-0 h-full w-full">
                                                        {children}
                                                    </div>
                                                )}
                                                onReady={() =>
                                                    setImageHasLoaded(true)
                                                }
                                                onError={() =>
                                                    setImageHasLoaded(true)
                                                }
                                            />
                                        )}
                                    </div>
                                )}

                            {/* Image */}
                            {effectiveSrc &&
                                !isEmbedCode(effectiveSrc) &&
                                (!enableVideo || !isEditingVideo) && (
                                    <img
                                        ref={imgElRef}
                                        className={`h-full w-full ${previewFitClass} rounded-md ${
                                            showPositionControl
                                                ? ''
                                                : 'object-left'
                                        } transition-opacity duration-300 ${
                                            imageHasLoaded
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                        }`}
                                        style={
                                            previewObjectPosition
                                                ? {
                                                      objectPosition:
                                                          previewObjectPosition,
                                                  }
                                                : undefined
                                        }
                                        src={effectiveSrc}
                                        alt="Uploaded Image"
                                        onLoad={() => setImageHasLoaded(true)}
                                    />
                                )}
                        </div>
                    </div>
                )}

                {/* === CONTROLS column === */}
                <div className={controlsClassName}>
                    <div className={controlsInnerClassName}>
                        {showActionButtons ? (
                            <div
                                className={
                                    actionButtonsInline
                                        ? 'flex flex-wrap items-center gap-2'
                                        : 'flex flex-col items-start space-y-2'
                                }
                            >
                                <div className="flex items-center gap-2">
                                    <label
                                        htmlFor={key}
                                        className="relative cursor-pointer rounded-md bg-primary-700 px-2 py-1 text-xs font-semibold leading-5 text-white shadow-sm transition-colors duration-200 ease-in-out hover:bg-primary-800"
                                    >
                                        <span>Upload a file</span>
                                        <input
                                            ref={uploadInputRef}
                                            id={key}
                                            name={key}
                                            type="file"
                                            className="sr-only"
                                            accept="image/*"
                                            onChange={(event) =>
                                                handleImageUpload(event)
                                            }
                                            disabled={is_image_uploading}
                                        />
                                    </label>
                                </div>

                                {showAssetPickerButton ? (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            onTogglePanel &&
                                            onTogglePanel(localSrc)
                                        }
                                        className="relative cursor-pointer rounded-md border border-primary-700 bg-white px-2 py-1 text-xs font-semibold leading-5 text-primary-700 shadow-sm transition-colors duration-200 ease-in-out hover:bg-primary-700 hover:text-white"
                                    >
                                        Select an Asset
                                    </button>
                                ) : null}
                            </div>
                        ) : null}

                        {!showActionButtons && uploadInputRef ? (
                            <input
                                ref={uploadInputRef}
                                id={key}
                                name={key}
                                type="file"
                                className="sr-only"
                                accept="image/*"
                                onChange={handleImageUpload}
                                disabled={is_image_uploading}
                            />
                        ) : null}

                        {showPositionControl && (
                            <ImagePositionControl
                                position={position}
                                onChange={handlePositionChange}
                            />
                        )}
                    </div>
                </div>

                {isStackedLayout && midContent ? (
                    <div className="w-full">{midContent}</div>
                ) : null}
            </div>
        </div>
    );
}

export default ImageUpload;
